import { Fragment } from 'react';
import { PNO_LOGO_WIDTH_IN, PNO_LOGO_MAX_HEIGHT_IN, PNO_LOGO_GAP_BELOW_IN } from '../../exporters/pageGeometry';
import {
    EDITOR_PAGE_BREAK_SLOT_PX,
    EDITOR_PAGE_END_SLOT_PX,
    PAGE1_CLEAR_ABOVE_LOGO_PX,
    type PageBreakSlot,
} from '../../hooks/usePageBoundaries';
import styles from './PageBreakSlotOverlay.module.scss';

// RECUR-27604 logo-box fix ("looks a bit broad" vs the PDF): the img's OWN
// box, in content px, set directly on the <img> itself — not 100% of the
// `.band` ancestor (the old approach, which only ever matched the PDF's box
// by coincidence: `.band` and this img always resolved to the identical
// size either way, but nothing pinned that equivalence). Mirrors
// exportToPdfServer.ts's `.pdf-pno-logo img` rule literally (width AND
// height both set on the img), so a wide (e.g. 5:1) lockup renders at the
// SAME width here as in the exported PDF. `PNO_LOGO_WIDTH_IN * 96` alone is
// `105.60000000000001` (1.1 has no exact binary representation) — rounded
// to 1dp so the rendered inline style is a clean `105.6px`, bit-for-bit the
// pixel equivalent of the PDF rule's own `1.1in`.
const PNO_LOGO_WIDTH_PX = Math.round(PNO_LOGO_WIDTH_IN * 96 * 10) / 10; // 105.6
const PNO_LOGO_MAX_HEIGHT_PX = Math.round(PNO_LOGO_MAX_HEIGHT_IN * 96 * 10) / 10; // 48
const LOGO_IMG_STYLE = { width: PNO_LOGO_WIDTH_PX, height: PNO_LOGO_MAX_HEIGHT_PX };

// RECUR-27604 approved visual spec (mock v2): the logo is vertically
// CENTERED within the enlarged `EDITOR_PAGE_BREAK_SLOT_PX` seam slot
// (block-kind AND row-kind) instead of flush against the slot's own top —
// 21px of air above and below the 48px logo. `band.top` (from
// `usePageBoundaries`) continues to mark the SLOT's own top (the seam
// hairline + "Page N" chip position, and — for a row slot — where the
// mask/edges are anchored); only the LOGO's own rendering position is offset
// from it.
const SEAM_LOGO_TOP_OFFSET_PX = (EDITOR_PAGE_BREAK_SLOT_PX - PNO_LOGO_MAX_HEIGHT_IN * 96) / 2; // 21

// Mid-table band cleanup (RECUR-27604, editor-visual follow-up to phase 4);
// widened to full-width in §11 (rev 6): a row-kind slot is cell padding
// INSIDE the table, so without a mask the row's own top border AND every
// vertical column-separator line run straight through it — masking only the
// host TABLE's own row-width (the pre-§11 shape) still left a narrower
// table's grid lines/borders visible beside it. 2px is left uncovered at the
// mask's top so PageBoundaryOverlay's own seam hairline (drawn at this SAME
// `top`, see this module's scss z-index note) stays visible instead of being
// painted over; the remaining 1px is the synthetic table-top edge that
// recreates the PDF continuation page's fresh border where the row's real
// content actually starts.
const MASK_TOP_CLEARANCE_PX = 2;
const EDGE_THICKNESS_PX = 1;
const MASK_HEIGHT_PX = EDITOR_PAGE_BREAK_SLOT_PX - MASK_TOP_CLEARANCE_PX - EDGE_THICKNESS_PX; // 87

// RECUR-27604 §13.3(a) — the END mask leaves the (relocated) bottom edge's
// own 1px visible, unlike the TOP mask's 2px clearance (which additionally
// has to leave room for the seam hairline drawn by PageBoundaryOverlay at
// the SAME Y as the top slot's own top). `EDGE_THICKNESS_PX` covers both.
const END_MASK_HEIGHT_PX = EDITOR_PAGE_END_SLOT_PX - EDGE_THICKNESS_PX; // 39

// RECUR-27604 §13.1: page 1's own injected clearance zone — 28px clear +
// the 48px logo + the SAME 18px of air the PDF uses before the first block
// (mirrors PAGE1_TOTAL_SPACE_PX, usePageBoundaries.ts — not exported from
// there since it's a private page-1-specific derivation; re-derived here
// from the SAME public constants so the two can't drift silently, same
// acceptable duplication pattern as CONTENT_TOP_INSET_PX, see that file's
// own risk note).
const PAGE1_SLOT_HEIGHT_PX = PAGE1_CLEAR_ABOVE_LOGO_PX + PNO_LOGO_MAX_HEIGHT_IN * 96 + PNO_LOGO_GAP_BELOW_IN * 96; // 94

interface PageBreakSlotOverlayProps {
    /**
     * RECUR-27604 §11.2: optional now — the slot (mask/edges) renders
     * regardless of the logo flags; the logo `<img>` is the only piece that's
     * still logo-gated. `PlateEditor` passes `bandActive ? pnoLogoUrl :
     * undefined`, and this component renders the `<img>` iff `logoUrl` is set.
     */
    logoUrl?: string;
    /**
     * One `PageBreakSlot` per page top that ACTUALLY has an injected
     * `EDITOR_PAGE_BREAK_SLOT_PX` slot behind it — derived by
     * `usePageBoundaries`, the only place that knows what it painted
     * (RECUR-27604 §8's per-row hostability skip, §8.5, is DOM-dependent and
     * cannot be re-derived from a `kinds` array alone; §12's crossing-content
     * fallback, same reasoning — a seam whose slot Y is crossed by anything
     * other than its own host, e.g. a columns block's own resize-handle
     * chrome, is excluded from `bands` at the SAME source, `pageStartUnits.ts`'s
     * `computePageGapTargets`). Page 1 is not included; this component adds it.
     */
    bands: PageBreakSlot[];
    /**
     * RECUR-27604 §13.1 — the content column's own box (contentEl-relative,
     * `usePageBoundaries`'s top-level `pageLeft`/`pageWidth`), used to size a
     * `'page1'`/`'block'`/`'endOnly'` slot's hit shield — none of these carry
     * a per-band `pageLeft`/`pageWidth` (only `'row'` does, for its mask).
     * `<= 0` (jsdom default / not yet measurable) falls back to spanning the
     * overlay's own full width, mirroring `maskBoxStyle`'s row-kind fallback.
     */
    pageLeft?: number;
    pageWidth?: number;
}

/**
 * Where the LOGO ITSELF should render for a given slot — `band.top` always
 * marks the SLOT's own top (seam hairline / "Page N" chip / mask anchor);
 * page 1 places the logo `PAGE1_CLEAR_ABOVE_LOGO_PX` below the card's top
 * edge (asymmetric — 28px clear above, 18px air below, mock v2), while a
 * seam (block or row-kind) centers it in the enlarged slot
 * (`SEAM_LOGO_TOP_OFFSET_PX`, symmetric air above/below).
 */
function logoTopPx(band: PageBreakSlot): number {
    return band.kind === 'page1' ? band.top : band.top + SEAM_LOGO_TOP_OFFSET_PX;
}

/**
 * The mask's own left/width (RECUR-27604 §11.3, amended by §12.5): the
 * CONTENT COLUMN (`band.pageLeft`/`band.pageWidth`, measured once per pass in
 * the hook from `contentEl`) rather than the host table's own box — a
 * narrower table would otherwise leave grid lines/borders visible beside it
 * at the seam. This full-width mask is safe to paint ONLY because `bands`
 * already guarantees the slot machinery owns EVERY box crossing the slot Y —
 * `computePageGapTargets`'s §12 crossing-content fallback suppresses the
 * whole slot (no `bands` entry at all) whenever anything else shares the
 * band, so within that guarantee the padded row spans every column of its
 * table and that table's content sits uniformly below the slot. jsdom
 * caveat (§11.6): `clientWidth` and computed padding are 0 there, so
 * `pageWidth` resolves to `<= 0` in any jest fixture that doesn't explicitly
 * define them — falls back to the host box (today's/pre-§11 behaviour)
 * rather than ever rendering a zero-width mask.
 */
function contentColumnBoxStyle(
    pageLeft: number | undefined,
    pageWidth: number | undefined,
    fallback: { left: string; width: string },
): { left: string; width: string } {
    if (pageWidth != null && pageWidth > 0 && pageLeft != null) {
        return { left: `${pageLeft}px`, width: `${pageWidth}px` };
    }
    return fallback;
}

/** Full-content-width fallback (RECUR-27604 §13.1) for a slot with no host table box to fall back to (`'page1'`/`'block'`/`'endOnly'`). */
const FULL_WIDTH_FALLBACK = { left: '0px', width: '100%' };

/**
 * A transparent hit shield (RECUR-27604 §13.1) — real pointer target
 * (`pointer-events: auto`) over an otherwise-inert painted slot, so a click
 * there is intercepted before it can reach the editable cell padding/margin
 * beneath. `preventDefault` stops focus/caret placement and native selection
 * start; `stopPropagation` is EQUALLY required — without it the event
 * bubbles to `PlateEditor.tsx`'s `focusEndOnPaddingClick`, whose "click
 * landed outside `[data-slate-editor]`" branch (true for every click on this
 * overlay — it's a DOM SIBLING of `<PlateContent>`, never a descendant)
 * would `select(end)` + `focus()`, jumping the caret to the document end.
 */
function stopClickThrough(e: React.MouseEvent): void {
    e.preventDefault();
    e.stopPropagation();
}

function HitShield({ box }: { box: { left: string; width: string; top: string; height: string } }) {
    return <div className={styles.hitShield} style={box} onMouseDown={stopClickThrough} />;
}

/**
 * Renders a single slot's TOP-side pieces (the page BELOW this boundary —
 * `band.kind`/`hostLeft`/`hostWidth`/`pageLeft`/`pageWidth`) plus its
 * END-side pieces (the page ABOVE it — `band.endSlot`), both independent
 * (RECUR-27604 §13.3): a boundary can have either, both, or (page1 only)
 * neither's mask/edge machinery while still needing a shield for whichever
 * side is actually painted.
 *
 * END-side geometry (§13.3(a)): a `'row'`-kind `endSlot` gets its own
 * closing edge at `slotTop - EDITOR_PAGE_END_SLOT_PX` and a 39px mask
 * filling `[slotTop - 39, slotTop)`, using the END slot's OWN host table box
 * (independent of the TOP slot's — the two can legitimately be different
 * tables, or one/both blocks). When an end slot is `'row'`-kind, it also
 * takes over the pre-existing "bottom edge coincident with the hairline"
 * responsibility from the TOP-side rendering below (`suppressLegacyBottomEdge`)
 * — that edge, previously always drawn at `slotTop` for a row-kind TOP slot
 * regardless of any end-slot concept, now only draws there when there is
 * genuinely nothing else claiming that Y (no end slot, or a block-kind one).
 * A shield is emitted whenever `endSlot` exists at all (even `'block'`-kind,
 * which has no mask/edge of its own) — the injected `margin-bottom` space
 * still needs interception (§13.1).
 */
function renderBand(band: PageBreakSlot, index: number, logoUrl: string | undefined, pageColumnBox: { left: string; width: string }) {
    const pieces: React.ReactNode[] = [];
    // RECUR-27852 — `band.top` now carries the TOP host's MEASURED slot anchor
    // (the Y where its injected top padding actually rendered — see
    // usePageBoundaries' `liveBoundaries` override), so every top-side piece
    // below stays pinned to the real rendered top margin even when the model's
    // own seam drifts a few rows off. The end-side pieces anchor separately to
    // `band.endSlotSeam` (the end host's own measured bottom) further down,
    // since a drifted end host can render well away from `band.top`.
    const slotTop = band.top;
    const isRowTop = band.kind === 'row' && band.hostLeft != null && band.hostWidth != null;
    const hostBoxStyle = isRowTop ? { left: `${band.hostLeft}px`, width: `${band.hostWidth}px` } : null;
    // Row-kind keeps its exact pre-§13 mask sizing (band's OWN pageLeft/
    // pageWidth, falling back to its host table box); every other kind has
    // no per-band pageLeft/pageWidth to read, so its shield falls back to
    // the TOP-LEVEL `pageColumnBox` (the overlay's own `pageLeft`/`pageWidth`
    // props, §13.1).
    const topContentColumnStyle = hostBoxStyle ? contentColumnBoxStyle(band.pageLeft, band.pageWidth, hostBoxStyle) : pageColumnBox;
    const suppressLegacyBottomEdge = band.endSlot?.kind === 'row';

    if (band.kind !== 'endOnly') {
        // Hit shield (RECUR-27604 §13.1) — the FULL slot, including the 2px
        // hairline clearance and the band/edge rows, at content-column width.
        pieces.push(
            <HitShield
                key={`top-shield-${index}`}
                box={{ ...topContentColumnStyle, top: `${slotTop}px`, height: `${EDITOR_PAGE_BREAK_SLOT_PX}px` }}
            />,
        );

        if (hostBoxStyle) {
            const maskTop = slotTop + MASK_TOP_CLEARANCE_PX;
            // Slot mask: repaints the CONTENT COLUMN (§11.3) as blank page
            // space, so every column grid line / border crossing the
            // injected slot stops showing through it — not just the host
            // table's own row-width.
            pieces.push(
                <div
                    key={`mask-${index}`}
                    className={styles.mask}
                    style={{ ...topContentColumnStyle, top: `${maskTop}px`, height: `${MASK_HEIGHT_PX}px` }}
                    onMouseDown={stopClickThrough}
                />,
            );
            if (!suppressLegacyBottomEdge) {
                // Legacy bottom edge (RECUR-27604 §11.4): at the slot's OWN
                // top — coincident with PageBoundaryOverlay's seam hairline
                // BY DESIGN. Suppressed only when an independent 'row'-kind
                // end slot is ALSO active for this boundary — that end slot
                // draws its OWN closing edge at the actually-relocated Y
                // (§13.3(a)) instead.
                pieces.push(
                    <div
                        key={`edge-bottom-${index}`}
                        className={styles.edge}
                        style={{ ...hostBoxStyle, top: `${slotTop}px` }}
                        onMouseDown={stopClickThrough}
                    />,
                );
            }
            // Synthetic TOP edge (existing): a single clean border line at
            // the mask's bottom edge, recreating the PDF continuation page's
            // "fresh table" look right where the row's real content actually
            // starts.
            pieces.push(
                <div
                    key={`edge-top-${index}`}
                    className={styles.edge}
                    style={{ ...hostBoxStyle, top: `${maskTop + MASK_HEIGHT_PX}px` }}
                    onMouseDown={stopClickThrough}
                />,
            );
        }

        if (logoUrl) {
            pieces.push(
                <div key={`band-${index}`} className={styles.band} style={{ top: `${logoTopPx(band)}px` }} onMouseDown={stopClickThrough}>
                    <img className={styles.logo} src={logoUrl} alt="" draggable={false} style={LOGO_IMG_STYLE} />
                </div>,
            );
        }
    }

    if (band.endSlot) {
        // RECUR-27852 — anchor the end slot to the end host's OWN measured
        // bottom (`endSlotSeam`), not `slotTop − 40`: the end host can render
        // well above the (top-anchored) `slotTop` when the model over-paginates,
        // so pinning to `slotTop` would paint the end mask over live rows. Falls
        // back to the pre-existing `slotTop − 40` when unmeasured (jsdom).
        const endBase = band.endSlotSeam ?? slotTop;
        const endTop = endBase - EDITOR_PAGE_END_SLOT_PX;
        const isRowEnd = band.endSlot.kind === 'row' && band.endSlot.hostLeft != null && band.endSlot.hostWidth != null;
        const endHostBoxStyle = isRowEnd ? { left: `${band.endSlot.hostLeft}px`, width: `${band.endSlot.hostWidth}px` } : null;
        const endContentColumnStyle = endHostBoxStyle
            ? contentColumnBoxStyle(band.pageLeft, band.pageWidth, endHostBoxStyle)
            : pageColumnBox;

        // End hit shield — the FULL end slot, whether or not it has a visible
        // mask/edge (a 'block'-kind end slot is still real `margin-bottom`
        // space that needs interception, §13.1).
        pieces.push(
            <HitShield
                key={`end-shield-${index}`}
                box={{ ...endContentColumnStyle, top: `${endTop}px`, height: `${EDITOR_PAGE_END_SLOT_PX}px` }}
            />,
        );

        if (endHostBoxStyle) {
            // Closing edge (RECUR-27604 §13.3(a)): the outgoing fragment's
            // real border, at the START of the end slot.
            pieces.push(
                <div
                    key={`end-edge-${index}`}
                    className={styles.edge}
                    style={{ ...endHostBoxStyle, top: `${endTop}px` }}
                    onMouseDown={stopClickThrough}
                />,
            );
            // End mask: blank content-column space between the closing edge
            // and the seam hairline (leaving the edge's own 1px visible).
            pieces.push(
                <div
                    key={`end-mask-${index}`}
                    className={styles.mask}
                    style={{ ...endContentColumnStyle, top: `${endTop + EDGE_THICKNESS_PX}px`, height: `${END_MASK_HEIGHT_PX}px` }}
                    onMouseDown={stopClickThrough}
                />,
            );
        }
    }

    return pieces.length > 0 ? <Fragment key={index}>{pieces}</Fragment> : null;
}

/**
 * Editor-only visual indicator (RECUR-27604 phase 4, "full-parity" revision;
 * §11 "always-on full-width page-start break", rev 6 — renamed from
 * `PnoLogoBandOverlay`, since its job is no longer "the PNO band" but "the
 * page-break slot, which may contain a band"; §13 adds page-END padding and
 * makes every painted slot INERT to clicks via a hit shield): at every
 * hosted seam of a page-gap document, paints the SAME `EDITOR_PAGE_BREAK_SLOT_PX`
 * slot the PDF's physical page break reads as — a closing edge on the
 * outgoing fragment, a full-content-width clean gap, and a fresh opening
 * edge on the resuming fragment — in the SAME per-page slot
 * `usePageBoundaries.ts`'s `applyPageGapStyles` already reserves, PLUS a
 * `EDITOR_PAGE_END_SLOT_PX` page-bottom margin on the page ABOVE it when that
 * last unit is hostable (§13.3).
 *
 * The slot itself renders regardless of the logo flags (§11.1/§11.2):
 * `usePageBoundaries` populates `bands` whenever `showPageGaps` is on, full
 * stop. The logo `<img>` is the ONLY logo-gated piece — `PlateEditor` passes
 * `logoUrl={bandActive ? pnoLogoUrl : undefined}`, and this component renders
 * an `<img>` iff `logoUrl` is set. With the logo off, the editor still shows
 * page-break padding the PDF itself doesn't contain — a deliberate,
 * user-approved choice: page CHROME visualising where the break is, not a
 * claim about PDF content (page starts still agree exactly, since the model
 * never sees this paint — see `usePageBoundaries.ts`'s own doc comment).
 *
 * Full parity/chrome is only reachable where a real DOM host exists to carry
 * the injected space: a `'block'`-kind page start (a live top-level element
 * whose own margin-top can grow), or a `'row'`-kind (mid-table) start whose
 * `<tr>` can carry the slot as cell padding (RECUR-27604 §8.1) — UNLESS that
 * row carries an inline `height` of its own (§8.5, un-hostable), OR its slot
 * Y is crossed by anything other than its own host (§12). A `'line'`-kind
 * (mid-paragraph) start has no host at all, so those seams get NOTHING (no
 * slot, no mask, no edges, no logo) rather than one painted over the
 * author's text; `PageBoundaryOverlay`'s own bare hairline + chip is all
 * that marks them. The SAME hostability rules govern the independent
 * page-END slot on the page above (§13.3) — a `kind: 'endOnly'` band has no
 * top-side rendering at all, only an end mask/edge/shield. This component
 * trusts `bands` blindly: slot <=> injected slot, by construction (N-5) —
 * every hostability predicate is evaluated exactly once, inside
 * `computePageGapTargets`/`computePageEndGapTargets`, never re-derived here.
 *
 * Every painted piece (mask/edge/band AND the new hit shields) is
 * `pointer-events: auto` (RECUR-27604 §13.1) — the overlay ROOT stays
 * `pointer-events: none` so gutters keep passing clicks through, but a shield
 * sits over the FULL slot (real editable cell/margin padding underneath) so
 * the caret/hover/click never reach it; see `stopClickThrough`'s own doc for
 * why `stopPropagation` there is load-bearing.
 *
 * Absolutely-positioned sibling of `<PlateContent>` inside `.contentWrap`
 * (see `PlateEditor.tsx`'s mount + `.contentWrap`'s own `position: relative`)
 * — never a Slate/Plate node, outside both PDF exporters' clone roots, so it
 * can never affect the document value, selection, save/load, or PDF export.
 * Not folded into `PageBoundaryOverlay`: that component early-returns `null`
 * on zero boundaries (a 1-page report would then lose its page-1 slot) and
 * its own "one consistent marker" contract/tests pin a markup shape with no
 * room for a logo/mask/edges/shield.
 */
export function PageBreakSlotOverlay({ logoUrl, bands, pageLeft, pageWidth }: PageBreakSlotOverlayProps) {
    // Page 1's `band.top` IS its logo's own top (`logoTopPx` is a no-op for
    // `kind: 'page1'`) — `usePageBoundaries.ts` injects the matching DISPLAY
    // gap on the first block (logo-gated, §11.2) so the card's built-in inset
    // plus that gap adds up to PAGE1_CLEAR_ABOVE_LOGO_PX + the 48px logo + the
    // PDF's own 18px air before the first block (mock v2) — page 1 is never a
    // page break, so it never gets a mask/edges/end-slot either way. Every
    // LATER slot is one of the already-filtered `bands` entries.
    const allBands: PageBreakSlot[] = [{ top: PAGE1_CLEAR_ABOVE_LOGO_PX, kind: 'page1' }, ...bands];
    const pageColumnBox = contentColumnBoxStyle(pageLeft, pageWidth, FULL_WIDTH_FALLBACK);

    return (
        <div
            className={styles.overlay}
            aria-hidden="true"
            contentEditable={false}
            style={{ '--pno-band-w': `${PNO_LOGO_WIDTH_IN}in`, '--pno-band-h': `${PNO_LOGO_MAX_HEIGHT_IN}in` } as React.CSSProperties}
        >
            {/* Keyed by INDEX, not `top`: two slots can legitimately share a Y
                (a zero-height/degenerate boundary), and a duplicate key would
                drop one. */}
            {allBands.map((band, index) =>
                band.kind === 'page1'
                    ? logoUrl && (
                          <Fragment key="page1">
                              {/* Page 1's own hit shield (§13.1): the injected
                                  clearance only physically exists once the
                                  logo is on (the built-in 73px inset needs no
                                  extra interception on its own — no injected
                                  space exists there without a logo). */}
                              <HitShield box={{ ...pageColumnBox, top: '0px', height: `${PAGE1_SLOT_HEIGHT_PX}px` }} />
                              <div className={styles.band} style={{ top: `${PAGE1_CLEAR_ABOVE_LOGO_PX}px` }} onMouseDown={stopClickThrough}>
                                  <img className={styles.logo} src={logoUrl} alt="" draggable={false} style={LOGO_IMG_STYLE} />
                              </div>
                          </Fragment>
                      )
                    : renderBand(band, index, logoUrl, pageColumnBox),
            )}
        </div>
    );
}
